// frontend/src/components/staff/PromoArchiveConfirm.js
//
// PROMO-RF-F — Staff Promo Code Management: ARCHIVE confirmation prompt.
// Mounts behind the "Archive" action that RF-F adds into the
// StaffPromoManagementPage detail-drawer `drawer-footer-actions-slot` seam
// RF-E reserved.
//
// Story:    docs/backlog/PROMO-RF-F-staff-promo-create-and-archive-form.md
// ADR:      docs/architecture/ADR-029-promo-reframe-reconcile.md
//             D2 — cited; archive is a Stripe-state change only, no in-app
//                  customer discount math.
//             D5 — consumes the RF-B archive contract VERBATIM (←RF-B/#181).
//
// RF-B archive contract (PR #181 — VERBATIM):
//     POST /api/staff/promo-codes/:promotionCodeId/archive   (no body)
//       200 -> { success: true, data: <shaped, active:false> }
//       4xx -> { success: false, error, reason }   (ADR-029 D3)
//   Stripe `active: false` — a DEACTIVATE, never a destructive delete. The
//   code is preserved in Stripe and the action is reversible-by-recreate
//   only — the confirmation copy makes that explicit (AC Scenario 3).
//
// Scope (AC Scenario 3): confirm prompt -> RF-B archive -> parent updates
//   the list to show the code as archived. NO in-house write (Scenario 5).

import React from "react";
import "./PromoArchiveConfirm.css";

const PromoArchiveConfirm = ({
  open,
  code, // display-only: the code string being archived
  onConfirm, // () => Promise<void>
  onCancel,
  submitting,
  errorMessage,
}) => {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting && typeof onCancel === "function") {
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel, submitting]);

  if (!open) return null;

  return (
    <>
      <div
        className="promo-archive-confirm__backdrop"
        onClick={submitting ? undefined : onCancel}
        aria-hidden="true"
        data-testid="promo-archive-confirm-backdrop"
      />
      <div
        className="promo-archive-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-archive-confirm-heading"
        data-testid="promo-archive-confirm"
      >
        <header className="promo-archive-confirm__header">
          <h2
            id="promo-archive-confirm-heading"
            className="promo-archive-confirm__heading"
          >
            Archive this promo code?
          </h2>
          <button
            type="button"
            className="promo-archive-confirm__close"
            onClick={onCancel}
            disabled={submitting}
            aria-label="Close archive confirmation"
            data-testid="promo-archive-confirm-close"
          >
            &times;
          </button>
        </header>

        <div className="promo-archive-confirm__body">
          {errorMessage && (
            <div
              className="promo-archive-confirm__error"
              role="alert"
              data-testid="promo-archive-confirm-error"
            >
              {errorMessage}
            </div>
          )}

          <p className="promo-archive-confirm__lead">
            <span
              className="promo-archive-confirm__code"
              data-testid="promo-archive-confirm-code"
            >
              {code || "this code"}
            </span>{" "}
            will be deactivated in Stripe.
          </p>

          <ul className="promo-archive-confirm__impact">
            <li>Customers can no longer apply it at Stripe checkout.</li>
            <li>
              It is <strong>not deleted</strong> — the code stays in Stripe
              and is reversible only by recreating a code with the same
              definition.
            </li>
            <li>Existing redemptions and usage history are unaffected.</li>
          </ul>
        </div>

        <footer className="promo-archive-confirm__footer">
          <button
            type="button"
            className="promo-archive-confirm__cancel"
            onClick={onCancel}
            disabled={submitting}
            data-testid="promo-archive-confirm-cancel"
          >
            Keep active
          </button>
          <button
            type="button"
            className="promo-archive-confirm__confirm"
            onClick={onConfirm}
            disabled={submitting}
            data-testid="promo-archive-confirm-confirm"
          >
            {submitting ? "Archiving…" : "Archive code"}
          </button>
        </footer>
      </div>
    </>
  );
};

export default PromoArchiveConfirm;
